import { useMemo, useState } from "react";
import { Eye, EyeOff, ImageIcon, Pencil, Plus, Trash2, Upload, X } from "lucide-react";
import { useSession } from "@/auth/SessionProvider";
import { CrmSocialPlatformIcon } from "@/components/crm/CrmSocialPlatformIcon";
import {
  CRM_LEAD_CHANNEL_FIELD_GROUP_LABELS,
  crmLeadChannelDetailKinds,
  crmLeadFieldBelongsToChannelGroup,
  crmLeadFieldIsHidden,
  type CrmLeadChannelFieldGroupId,
  type CrmLeadFieldDefinition,
  type CrmLeadFieldSectionId,
  type CrmLeadModuleFieldsDocument,
} from "@/lib/crmLeadModuleFields";
import {
  extractOrgIdFromTenantAssetUrl,
  uploadCrmLeadFieldOptionIcon,
} from "@/lib/crmLeadFieldIcon";
import { socialPlatformFromOptionValue } from "@/lib/crmContactChannels";
import { CountryCodePicker, formatCountryCodeLabel } from "@/components/crm/CountryCodePicker";
import { AddressCountryPicker } from "@/components/crm/AddressCountryPicker";
import { getCountryCodeById } from "@/lib/countryCodes";
import { formatIsoCountryLabel, getIsoCountryByCode } from "@/lib/address/isoCountries";

const fieldClass =
  "w-full rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-white/20 dark:bg-black/20";

type CrmLeadModuleChannelFieldDetailPanelProps = {
  sectionId: CrmLeadFieldSectionId;
  groupId: CrmLeadChannelFieldGroupId;
  sections: CrmLeadModuleFieldsDocument["sections"];
  onChange: (next: CrmLeadModuleFieldsDocument["sections"]) => void;
  canManage: boolean;
  disabled?: boolean;
};

function kindLabel(kind: CrmLeadFieldDefinition["kind"]): string {
  if (kind === "email_type") return "Email type";
  if (kind === "phone_type") return "Phone type";
  if (kind === "phone_country_default") return "Standard country code";
  if (kind === "address_country_default") return "Standard country";
  if (kind === "address_type") return "Address type";
  return "Social type";
}

function newDetailFieldId(sectionId: CrmLeadFieldSectionId, groupId: CrmLeadChannelFieldGroupId): string {
  return `${sectionId}_${groupId}_${Date.now().toString(36)}`;
}

function defaultKindForCreate(groupId: CrmLeadChannelFieldGroupId): CrmLeadFieldDefinition["kind"] {
  if (groupId === "email") return "email_type";
  if (groupId === "phone") return "phone_type";
  if (groupId === "address") return "address_type";
  return "social_type";
}

export function CrmLeadModuleChannelFieldDetailPanel({
  sectionId,
  groupId,
  sections,
  onChange,
  canManage,
  disabled,
}: CrmLeadModuleChannelFieldDetailPanelProps) {
  const session = useSession();
  const showSocialIcons = groupId === "social";

  const allSectionFields = sections[sectionId] ?? [];
  const groupFields = useMemo(
    () => allSectionFields.filter((f) => crmLeadFieldBelongsToChannelGroup(f, groupId)),
    [allSectionFields, groupId],
  );

  const orgId = useMemo(() => {
    const sessionOrgId = session.data?.user?.orgId?.trim() || null;
    if (sessionOrgId) return sessionOrgId;
    for (const field of groupFields) {
      const fromUrl = extractOrgIdFromTenantAssetUrl(field.optionIconUrl);
      if (fromUrl) return fromUrl;
    }
    for (const field of allSectionFields) {
      const fromUrl = extractOrgIdFromTenantAssetUrl(field.optionIconUrl);
      if (fromUrl) return fromUrl;
    }
    return null;
  }, [allSectionFields, groupFields, session.data?.user?.orgId]);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<CrmLeadFieldDefinition | null>(null);
  const [iconUploading, setIconUploading] = useState(false);
  const [iconError, setIconError] = useState<string | null>(null);

  const otherSectionFields = useMemo(
    () => allSectionFields.filter((f) => !crmLeadFieldBelongsToChannelGroup(f, groupId)),
    [allSectionFields, groupId],
  );

  const commitSectionFields = (nextGroupFields: CrmLeadFieldDefinition[]) => {
    onChange({ ...sections, [sectionId]: [...otherSectionFields, ...nextGroupFields] });
  };

  const startCreate = () => {
    const kind = defaultKindForCreate(groupId);
    const base: CrmLeadFieldDefinition = {
      id: newDetailFieldId(sectionId, groupId),
      label: kind === "phone_type" ? "New phone type" : kind === "email_type" ? "New email type" : kind === "address_type" ? "New address type" : "New social",
      kind,
      isStandard: false,
      optionValue: "",
      optionLabel: "",
    };
    setDraft(base);
    setEditingId(base.id);
  };

  const startEdit = (field: CrmLeadFieldDefinition) => {
    setDraft({ ...field });
    setEditingId(field.id);
  };

  const cancelEdit = () => {
    setDraft(null);
    setEditingId(null);
    setIconError(null);
    setIconUploading(false);
  };

  const handleIconFile = async (file: File | null) => {
    if (!file || !draft) return;
    if (!orgId) {
      setIconError("Open CRM from your organization dashboard to upload icons.");
      return;
    }
    setIconUploading(true);
    setIconError(null);
    try {
      const url = await uploadCrmLeadFieldOptionIcon(orgId, file);
      setDraft({ ...draft, optionIconUrl: url });
    } catch (e) {
      setIconError(e instanceof Error ? e.message : "Icon upload failed.");
    } finally {
      setIconUploading(false);
    }
  };

  const clearDraftIcon = () => {
    if (!draft) return;
    const { optionIconUrl: _removed, ...rest } = draft;
    setDraft(rest);
    setIconError(null);
  };

  const commitEdit = () => {
    if (!draft) return;
    const label = draft.label.trim();
    const id = draft.id.trim();
    if (!label || !id) return;
    const nextField = { ...draft, label, id };
    const exists = groupFields.some((f) => f.id === nextField.id);
    const nextList = exists
      ? groupFields.map((f) => (f.id === editingId ? nextField : f))
      : [...groupFields, nextField];
    commitSectionFields(nextList);
    cancelEdit();
  };

  const removeField = (id: string) => {
    commitSectionFields(groupFields.filter((f) => f.id !== id));
    if (editingId === id) cancelEdit();
  };

  const toggleFieldHidden = (id: string) => {
    const next = groupFields.map((f) =>
      f.id === id ? { ...f, hidden: f.hidden ? undefined : true } : f,
    );
    commitSectionFields(next);
    if (editingId === id && draft) {
      setDraft({ ...draft, hidden: draft.hidden ? undefined : true });
    }
  };

  const editing = draft !== null;
  const editingExisting =
    editingId !== null && groupFields.some((f) => f.id === editingId);
  const groupLabel = CRM_LEAD_CHANNEL_FIELD_GROUP_LABELS[groupId];
  const allowedKinds = crmLeadChannelDetailKinds(groupId);

  const sectionHelp = useMemo(() => {
    if (groupId === "email") {
      return "Email type options shown when users add company or contact emails on New Lead.";
    }
    if (groupId === "phone") {
      return "Standard country code and phone type options for New Lead phone rows.";
    }
    return "Social network options for New Lead social links.";
  }, [groupId]);

  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-600 dark:text-slate-400">{sectionHelp}</p>

      <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-white/10">
        <table className="min-w-full divide-y divide-slate-200 text-sm dark:divide-white/10">
          <thead className="bg-slate-50 dark:bg-white/5">
            <tr>
              {showSocialIcons ? (
                <th className="w-12 px-3 py-2 text-left font-semibold text-slate-700 dark:text-slate-200">Icon</th>
              ) : null}
              <th className="px-3 py-2 text-left font-semibold text-slate-700 dark:text-slate-200">Label</th>
              <th className="px-3 py-2 text-left font-semibold text-slate-700 dark:text-slate-200">Kind</th>
              <th className="px-3 py-2 text-right font-semibold text-slate-700 dark:text-slate-200">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-white/5">
            {groupFields.map((field) => (
              <tr key={field.id} className={crmLeadFieldIsHidden(field) ? "opacity-70" : undefined}>
                {showSocialIcons ? (
                  <td className="px-3 py-2">
                    {field.kind === "social_type" && field.optionValue ? (
                      <CrmSocialPlatformIcon
                        platform={socialPlatformFromOptionValue(field.optionValue)}
                        iconUrl={field.optionIconUrl}
                        orgId={orgId}
                        className="inline-flex h-7 w-7 shrink-0 items-center justify-center overflow-hidden rounded border border-slate-200 bg-white dark:border-white/15 dark:bg-black/30"
                      />
                    ) : (
                      <span className="inline-flex h-7 w-7 items-center justify-center text-slate-300 dark:text-slate-600">
                        <ImageIcon className="h-3.5 w-3.5" aria-hidden />
                      </span>
                    )}
                  </td>
                ) : null}
                <td className="px-3 py-2 text-slate-900 dark:text-white">
                  {field.label}
                  {field.isStandard ? (
                    <span className="ml-2 text-[10px] uppercase tracking-wide text-slate-400">Standard</span>
                  ) : null}
                  {crmLeadFieldIsHidden(field) ? (
                    <span className="ml-2 rounded bg-slate-200 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-600 dark:bg-white/10 dark:text-slate-300">
                      Hidden
                    </span>
                  ) : null}
                </td>
                <td className="px-3 py-2 text-slate-600 dark:text-slate-300">
                  {field.kind === "phone_country_default"
                    ? (() => {
                        const c = getCountryCodeById(field.optionValue ?? "");
                        return c ? `${c.flag} ${formatCountryCodeLabel(c)}` : field.optionLabel || "Not set";
                      })()
                    : kindLabel(field.kind)}
                </td>
                <td className="px-3 py-2">
                  <div className="flex justify-end gap-1">
                    {canManage ? (
                      <>
                        <button
                          type="button"
                          disabled={disabled || field.kind === "phone_country_default"}
                          aria-label={crmLeadFieldIsHidden(field) ? `Unhide ${field.label}` : `Hide ${field.label}`}
                          title={crmLeadFieldIsHidden(field) ? "Unhide on New Lead" : "Hide on New Lead"}
                          onClick={() => toggleFieldHidden(field.id)}
                          className="rounded-md border border-slate-300 p-1.5 text-slate-600 hover:bg-slate-50 disabled:opacity-40 dark:border-white/20 dark:text-slate-300"
                        >
                          {crmLeadFieldIsHidden(field) ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </button>
                        <button
                          type="button"
                          disabled={disabled}
                          aria-label={`Edit ${field.label}`}
                          onClick={() => startEdit(field)}
                          className="rounded-md border border-slate-300 p-1.5 text-slate-600 hover:bg-slate-50 dark:border-white/20 dark:text-slate-300"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          disabled={disabled || field.kind === "phone_country_default"}
                          aria-label={`Delete ${field.label}`}
                          onClick={() => removeField(field.id)}
                          className="rounded-md border border-slate-300 p-1.5 text-slate-600 hover:bg-slate-50 disabled:opacity-40 dark:border-white/20 dark:text-slate-300"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </>
                    ) : null}
                  </div>
                </td>
              </tr>
            ))}
            {!groupFields.length ? (
              <tr>
                <td colSpan={showSocialIcons ? 4 : 3} className="px-3 py-6 text-center text-slate-500">
                  No {groupLabel.toLowerCase()} options yet.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      {canManage ? (
        <button
          type="button"
          disabled={disabled || Boolean(editing)}
          onClick={startCreate}
          className="inline-flex items-center gap-1 rounded-md border border-indigo-200 bg-indigo-50 px-3 py-2 text-xs font-semibold text-indigo-800 hover:bg-indigo-100 disabled:opacity-50 dark:border-indigo-500/30 dark:bg-indigo-950/40 dark:text-indigo-200"
        >
          <Plus className="h-3.5 w-3.5" />
          Add option
        </button>
      ) : null}

      {editing && draft ? (
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 dark:border-white/10 dark:bg-white/5">
          <h3 className="text-sm font-semibold text-slate-900 dark:text-white">
            {editingExisting ? "Update option" : "Add option"}
          </h3>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <label className="text-xs font-medium text-slate-600 dark:text-slate-300">
              Label
              <input
                value={draft.label}
                disabled={disabled}
                onChange={(e) => setDraft({ ...draft, label: e.target.value })}
                className={`${fieldClass} mt-1`}
              />
            </label>
            <label className="text-xs font-medium text-slate-600 dark:text-slate-300">
              Field id
              <input
                value={draft.id}
                disabled={disabled || draft.isStandard || editingExisting}
                readOnly={editingExisting}
                onChange={(e) => setDraft({ ...draft, id: e.target.value })}
                className={`${fieldClass} mt-1${editingExisting ? " cursor-not-allowed opacity-80" : ""}`}
              />
              {editingExisting ? (
                <span className="mt-1 block text-[11px] font-normal text-slate-500 dark:text-slate-400">
                  Field id cannot be changed after the option is created.
                </span>
              ) : null}
            </label>
            {allowedKinds.length > 1 && !draft.isStandard ? (
              <label className="text-xs font-medium text-slate-600 dark:text-slate-300">
                Kind
                <select
                  value={draft.kind}
                  disabled={disabled}
                  onChange={(e) =>
                    setDraft({ ...draft, kind: e.target.value as CrmLeadFieldDefinition["kind"] })
                  }
                  className={`${fieldClass} mt-1`}
                >
                  {allowedKinds.map((k) => (
                    <option key={k} value={k}>
                      {kindLabel(k)}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
            {draft.kind === "phone_country_default" ? (
              <div className="sm:col-span-2">
                <p className="text-xs font-medium text-slate-600 dark:text-slate-300">Standard country</p>
                <div className="mt-1 max-w-md">
                  <CountryCodePicker
                    variant="field"
                    disabled={disabled}
                    value={draft.optionValue ?? "us"}
                    ariaLabel="Standard country code for new phone numbers"
                    onChange={(countryId) => {
                      const country = getCountryCodeById(countryId);
                      setDraft({
                        ...draft,
                        optionValue: countryId,
                        optionLabel: country ? formatCountryCodeLabel(country) : countryId,
                      });
                    }}
                  />
                </div>
              </div>
            ) : null}
            {draft.kind === "address_country_default" ? (
              <div className="sm:col-span-2">
                <p className="text-xs font-medium text-slate-600 dark:text-slate-300">Standard country</p>
                <div className="mt-1 max-w-md">
                  <AddressCountryPicker
                    disabled={disabled}
                    value={draft.optionValue ?? "us"}
                    ariaLabel="Standard country for new addresses"
                    onChange={(countryCode) => {
                      const country = getIsoCountryByCode(countryCode);
                      setDraft({
                        ...draft,
                        optionValue: countryCode,
                        optionLabel: country ? formatIsoCountryLabel(country) : countryCode,
                      });
                    }}
                  />
                </div>
              </div>
            ) : null}
            {draft.kind === "email_type" ||
            draft.kind === "phone_type" ||
            draft.kind === "address_type" ||
            draft.kind === "social_type" ? (
              <>
                <label className="text-xs font-medium text-slate-600 dark:text-slate-300">
                  Option value
                  <input
                    value={draft.optionValue ?? ""}
                    disabled={disabled}
                    onChange={(e) => setDraft({ ...draft, optionValue: e.target.value })}
                    className={`${fieldClass} mt-1`}
                  />
                </label>
                <label className="text-xs font-medium text-slate-600 dark:text-slate-300">
                  Option label
                  <input
                    value={draft.optionLabel ?? ""}
                    disabled={disabled}
                    onChange={(e) => setDraft({ ...draft, optionLabel: e.target.value })}
                    className={`${fieldClass} mt-1`}
                  />
                </label>
              </>
            ) : null}
            {draft.kind === "social_type" ? (
              <div className="sm:col-span-2">
                <p className="text-xs font-medium text-slate-600 dark:text-slate-300">Icon</p>
                <p className="mt-0.5 text-[11px] text-slate-500 dark:text-slate-400">
                  PNG, SVG, or WebP. Shown on New Lead social rows when set.
                </p>
                <div className="mt-2 flex flex-wrap items-center gap-3">
                  {draft.optionValue ? (
                    <CrmSocialPlatformIcon
                      platform={socialPlatformFromOptionValue(draft.optionValue)}
                      iconUrl={draft.optionIconUrl}
                      orgId={orgId}
                      className="inline-flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-md border border-slate-200 bg-white dark:border-white/15 dark:bg-black/30"
                    />
                  ) : (
                    <span className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-dashed border-slate-300 text-slate-400 dark:border-white/20">
                      <ImageIcon className="h-4 w-4" aria-hidden />
                    </span>
                  )}
                  <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-slate-300 px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50 dark:border-white/20 dark:text-slate-200 dark:hover:bg-white/5">
                    <Upload className="h-3.5 w-3.5" aria-hidden />
                    {iconUploading ? "Uploading…" : draft.optionIconUrl ? "Replace" : "Upload"}
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/webp,image/svg+xml,.png,.jpg,.jpeg,.webp,.svg"
                      className="sr-only"
                      disabled={disabled || iconUploading || !orgId}
                      onChange={(e) => {
                        const file = e.target.files?.[0] ?? null;
                        e.target.value = "";
                        void handleIconFile(file);
                      }}
                    />
                  </label>
                  {draft.optionIconUrl ? (
                    <button
                      type="button"
                      disabled={disabled || iconUploading}
                      onClick={clearDraftIcon}
                      className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-2.5 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50 dark:border-white/20 dark:text-slate-300"
                    >
                      <X className="h-3.5 w-3.5" aria-hidden />
                      Remove
                    </button>
                  ) : null}
                </div>
                {iconError ? <p className="mt-2 text-xs text-amber-700 dark:text-amber-300">{iconError}</p> : null}
                {!orgId ? (
                  <p className="mt-1 text-[11px] text-slate-500">Organization context is required to upload.</p>
                ) : null}
              </div>
            ) : null}
            <label className="flex items-center gap-2 pt-2 text-xs text-slate-600 dark:text-slate-300 sm:col-span-2">
              <input
                type="checkbox"
                checked={Boolean(draft.hidden)}
                disabled={disabled || draft.kind === "phone_country_default"}
                onChange={(e) => setDraft({ ...draft, hidden: e.target.checked ? true : undefined })}
              />
              Hidden on New Lead
            </label>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <button
              type="button"
              disabled={disabled}
              onClick={commitEdit}
              className="rounded-md bg-indigo-600 px-3 py-2 text-xs font-semibold text-white hover:bg-indigo-500"
            >
              Save option
            </button>
            <button
              type="button"
              disabled={disabled}
              onClick={cancelEdit}
              className="rounded-md border border-slate-300 px-3 py-2 text-xs font-semibold text-slate-700 dark:border-white/20 dark:text-slate-200"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : null}

      {!canManage ? (
        <p className="text-xs text-slate-500">Only account owners and admins can change field definitions.</p>
      ) : null}
    </div>
  );
}
